import { Collapsible as CollapsiblePrimitive } from 'radix-ui';
import * as React from 'react';

import { cn } from './utils/cn';

/**
 * Show/hide container for content that should expand inline. Pair with
 * {@link CollapsibleTrigger} and {@link CollapsibleContent}.
 *
 * @example
 * <Collapsible>
 *   <CollapsibleTrigger asChild>
 *     <Button variant="ghost">Show details</Button>
 *   </CollapsibleTrigger>
 *   <CollapsibleContent animated>
 *     <Stack gap={2}>{details}</Stack>
 *   </CollapsibleContent>
 * </Collapsible>
 */
function Collapsible({
  ...props
}: React.ComponentProps<typeof CollapsiblePrimitive.Root>) {
  return <CollapsiblePrimitive.Root data-slot="collapsible" {...props} />;
}

/** Toggle that opens/closes the parent {@link Collapsible}. Wrap a `Button` (or any clickable) with `asChild`. */
function CollapsibleTrigger({
  ...props
}: React.ComponentProps<typeof CollapsiblePrimitive.CollapsibleTrigger>) {
  return (
    <CollapsiblePrimitive.CollapsibleTrigger
      data-slot="collapsible-trigger"
      {...props}
    />
  );
}

/**
 * Content shown only when the parent {@link Collapsible} is open. Pass
 * `animated` to fade and slide the content open/closed; without it, content
 * snaps in and out instantly.
 */
function CollapsibleContent({
  animated = false,
  className,
  ...props
}: React.ComponentProps<typeof CollapsiblePrimitive.CollapsibleContent> & {
  animated?: boolean;
}) {
  return (
    <CollapsiblePrimitive.CollapsibleContent
      data-slot="collapsible-content"
      className={cn(
        animated &&
          'overflow-hidden data-open:animate-collapsible-down data-closed:animate-collapsible-up',
        className,
      )}
      {...props}
    />
  );
}

export { Collapsible, CollapsibleTrigger, CollapsibleContent };
